import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, filter, map, Observable, of, switchMap } from 'rxjs';

import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import { LocalStorageService, StorageKey } from '@blaze-shared/platform-data-access';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogService,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ElementLoadingIndicatorComponent,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';

import { ApiBuilderStore, initialState } from '../+state/api-builder.store';
import { ApiBuilderComponent } from '../api-builder/api-builder.component';
import { ApiBuilderAction, ApiBuilderCommonConfig } from '../api-builder.model';
import { ApiBuilderService } from '../services/api-builder.service';
import { areApiDefsEqual } from '../utils/api-builder-utils';

@UntilDestroy()
@Component({
  selector: 'blaze-api-builder-page',
  imports: [
    ApiBuilderComponent,
    AsyncPipe,
    ButtonBarComponent,
    CopyConnectorConfigEnvButtonComponent,
    ElementLoadingIndicatorComponent,
    EnvironmentIndicatorComponent,
    FormsModule,
    PanelComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './api-builder-page.component.html',
  styleUrls: ['./api-builder-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiBuilderPageComponent implements OnInit {
  private readonly apiBuilderService = inject(ApiBuilderService);
  private readonly apiBuilderStore = inject(ApiBuilderStore);
  private readonly dialogService = inject(DialogService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly panelRef = inject(PanelRef);

  accountId = '';
  connectorNameCtrl!: UntypedFormControl;
  upsertConnectorLabel$: Observable<string>;
  coupledConnector$ = this.apiBuilderStore.coupledConnector$;
  valid$: Observable<boolean>;
  savingConnector$: Observable<boolean>;
  actionsTested = false;
  showValidation = false;

  constructor() {
    this.accountId = this.localStorageService.get(StorageKey.AccountId) || '';
    this.connectorNameCtrl = new UntypedFormControl('');

    this.valid$ = this.apiBuilderStore.valid$;
    this.savingConnector$ = this.apiBuilderStore.savingConnector$;

    this.upsertConnectorLabel$ = this.apiBuilderStore.connectorId$.pipe(
      map((connectorId) => (connectorId ? 'Update Connector' : 'Create Connector')),
    );

    this.panelRef.setConfirmClose(() => {
      return combineLatest({
        originalConnector: this.apiBuilderStore.originalConnector$,
        connectorName: this.apiBuilderStore.connectorName$,
        common: this.apiBuilderStore.common$,
        actions: this.apiBuilderStore.actions$,
        savingConnector: this.savingConnector$,
      }).pipe(
        untilDestroyed(this),
        filter(({ savingConnector }) => !savingConnector),
        switchMap(({ originalConnector, connectorName, common, actions }) => {
          if (!this.shouldConfirmCloseDialog(originalConnector, connectorName, common, actions)) {
            return of(true);
          }

          return this.dialogService
            .openConfirmDialog({
              title: 'Unsaved changes',
              description:
                'You have unsaved changes. Are you sure you want to close the editor without saving?',
              cancelLabel: 'Cancel',
              confirmLabel: 'Yes',
            })
            .afterClosed();
        }),
      );
    });
  }

  ngOnInit(): void {
    this.apiBuilderStore.connectorName$.pipe(untilDestroyed(this)).subscribe((value) => {
      this.connectorNameCtrl.patchValue(value, { emitEvent: false });
    });
    this.connectorNameCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.apiBuilderStore.setConnectorName(value);
    });

    this.apiBuilderStore.actions$
      .pipe(map((actions) => actions.every((action) => action.data.isTested)))
      .subscribe((actionsTested) => {
        this.actionsTested = actionsTested;
      });
  }

  upsertConnector(): void {
    const doSave = (): void => {
      this.panelRef.setConfirmClose(null);
      this.apiBuilderStore.upsertConnector({
        accountId: this.accountId,
      });
    };

    if (this.actionsTested) {
      doSave();
      return;
    }

    this.showValidation = true;
    this.dialogService
      .openConfirmDialog({
        title: 'Configuration Is Incomplete',
        description:
          'One or more actions haven’t been tested.<br />Please test all actions for the connection to work.',
        cancelLabel: 'SAVE, TEST LATER',
        confirmLabel: 'TEST ACTIONS',
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          doSave();
        }
      });
  }

  copyConfiguration(connector?: ConnectorConfiguration): void {
    this.apiBuilderStore.initialize({
      connectorId: connector?.id,
      connectorName: connector?.name || '',
      ...this.apiBuilderService.mapApiDefinitionToStore(connector?.customApiDefinition),
    });
  }

  private shouldConfirmCloseDialog(
    connector: ConnectorConfiguration | null,
    connectorName: string,
    common: ApiBuilderCommonConfig,
    actions: ApiBuilderAction[],
  ): boolean {
    if (!connector) {
      return false;
    }

    const savedApiDef = connector.customApiDefinition;
    const currentApiDef = this.apiBuilderService.mapStoreToDefinition(common, actions);
    const defaultApiDef = this.apiBuilderService.mapStoreToDefinition(
      initialState.common,
      initialState.actions,
    );

    const isConnectorNameEqual = connector.name === connectorName;

    return !isConnectorNameEqual || !areApiDefsEqual(savedApiDef || defaultApiDef, currentApiDef);
  }
}
